<template>
  <div class="card mb-4">
    <div class="card-header pb-0">
      <h6>Produtos</h6>
      <div class="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <p class="text-sm mb-0">
          <span class="font-weight-bold">{{ produtos.length }}</span> produtos encontrados
        </p>
        <div class="d-flex gap-2 align-items-center flex-wrap">
          <div class="search-group">
            <i class="fas fa-search search-icon"></i>
            <input
              type="text"
              class="search-input"
              placeholder="Buscar produto..."
              v-model="searchQuery"
              @input="onSearchInput"
              @keyup.enter="performSearch"
            >
            <button
              v-if="searchQuery"
              class="search-clear"
              type="button"
              @click="clearSearch"
              title="Limpar busca"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>
          <button
            @click="showAddModal"
            class="btn btn-sm btn-custom-gradient mb-0"
          >
            Adicionar
          </button>
          <button
            @click="refreshList"
            class="btn btn-sm mb-0"
            :class="loading ? 'btn-secondary' : 'btn-custom-gradient'"
            :disabled="loading"
          >
           {{ loading ? 'Carregando...' : 'Atualizar' }}
         </button>
        </div>
      </div>
    </div>
    <div class="card-body px-0 pt-0 pb-2">
      <div v-if="error" class="alert alert-danger mx-3">
        {{ error }}
      </div>
      <div v-if="loading" class="text-center py-4">
        <div class="spinner-border spinner-teal" role="status">
          <span class="visually-hidden">Carregando...</span>
        </div>
      </div>
      <div v-else class="table-responsive p-0">
        <table class="table align-items-center mb-0">
          <thead>
            <tr>
              <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                Produto
              </th>
              <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                GTIN
              </th>
              <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                Categoria - API
              </th>
              <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                Categoria
              </th>
              <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                Preço
              </th>
              <th class="text-secondary opacity-7"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="produto in produtos" :key="produto.id">
              <td>
                <div class="d-flex px-2 py-1">
                  <div class="me-3" v-if="produto.image">
                    <img 
                      :src="produto.image" 
                      :alt="produto.description"
                      class="img-thumbnail"
                      style="width: 60px; height: 60px; object-fit: cover;"
                      @error="handleImageError"
                    >
                  </div>
                  <div v-else class="me-3 d-flex align-items-center justify-content-center" style="width: 60px; height: 60px; background: #f0f0f0; border-radius: 4px;">
                    <i class="fas fa-image text-muted"></i>
                  </div>
                  <div class="d-flex flex-column justify-content-center">
                    <h6 class="mb-0 text-sm">{{ produto.description || 'Sem descrição' }}</h6>
                    <p class="text-xs text-secondary mb-0">
                      ID: {{ produto.id }}
                    </p>
                  </div>
                </div>
              </td>
              <td class="align-middle text-sm">
                <span class="text-secondary text-xs font-weight-bold">
                  {{ produto.gtin || 'N/A' }}
                </span>
              </td>
              <td class="align-middle text-sm">
                <div class="d-flex align-items-center">
                  <span class="text-secondary text-xs font-weight-bold">
                    {{ getCategoryNameFromProduct(produto) }}
                  </span>
                  <i 
                    v-if="!hasCategoryApi(produto)" 
                    class="fas fa-edit ms-2 text-primary cursor-pointer" 
                    style="cursor: pointer; font-size: 12px;"
                    @click="openEditCategoryApiModal(produto)"
                    title="Editar categoria API"
                  ></i>
                </div>
              </td>
              <td class="align-middle text-sm">
                <span class="text-secondary text-xs font-weight-bold">
                  {{ getCategoryName(produto) }}
                </span>
              </td>
              <td class="align-middle text-center text-sm">
                <span class="text-success text-xs font-weight-bold" v-if="produto.price">
                  {{ produto.price }}
                </span>
                <div v-else-if="produto.min_price || produto.max_price || produto.avg_price">
                  <span class="text-success text-xs font-weight-bold d-block">
                    {{ formatPriceRange(produto) }}
                  </span>
                  <small class="text-muted d-block" v-if="produto.avg_price">
                    Média: R$ {{ formatPrice(produto.avg_price) }}
                  </small>
                </div>
                <span class="text-secondary text-xs" v-else>
                  N/A
                </span>
              </td>
              <td class="align-middle">
                <a
                  href="javascript:;"
                  class="text-secondary font-weight-bold text-xs"
                  data-toggle="tooltip"
                  data-original-title="Ver detalhes"
                  @click="viewDetails(produto)"
                >
                  Detalhes
                </a>
              </td>
            </tr>
            <tr v-if="produtos.length === 0 && !loading">
              <td colspan="6" class="text-center py-4">
                <p class="text-secondary mb-0">
                  {{ isSearching ? `Nenhum produto encontrado para "${searchQuery}"` : 'Nenhum produto encontrado' }}
                </p>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Controles de Paginação -->
    <div class="card-footer d-flex justify-content-between align-items-center">
      <div class="text-sm text-secondary">
        <template v-if="isSearching">
          {{ produtos.length }} resultado(s) para "{{ searchQuery }}"
        </template>
        <template v-else>
          Mostrando {{ produtos.length }} de {{ totalProducts }} produtos
          <span v-if="totalPages > 1">
            (Página {{ currentPage }} de {{ totalPages }})
          </span>
        </template>
      </div>

      <nav v-if="!isSearching" aria-label="Navegação de páginas">
        <ul class="pagination pagination-sm mb-0">
          <!-- Primeira página -->
          <li class="page-item" :class="{ disabled: currentPage === 1 }">
            <a class="page-link" href="javascript:;" @click="firstPage" aria-label="Primeira página">
              <i class="fas fa-angle-double-left"></i>
            </a>
          </li>
          
          <!-- Página anterior -->
          <li class="page-item" :class="{ disabled: currentPage === 1 }">
            <a class="page-link" href="javascript:;" @click="prevPage" aria-label="Página anterior">
              <i class="fas fa-angle-left"></i>
            </a>
          </li>
          
          <!-- Números das páginas -->
          <li 
            v-for="page in getPageNumbers()" 
            :key="page"
            class="page-item"
            :class="{ active: page === currentPage }"
          >
            <a class="page-link" href="javascript:;" @click="goToPage(page)">
              {{ page }}
            </a>
          </li>
          
          <!-- Página seguinte -->
          <li class="page-item" :class="{ disabled: currentPage === totalPages }">
            <a class="page-link" href="javascript:;" @click="nextPage" aria-label="Próxima página">
              <i class="fas fa-angle-right"></i>
            </a>
          </li>
          
          <!-- Última página -->
          <li class="page-item" :class="{ disabled: currentPage === totalPages }">
            <a class="page-link" href="javascript:;" @click="lastPage" aria-label="Última página">
              <i class="fas fa-angle-double-right"></i>
            </a>
          </li>
        </ul>
      </nav>
    </div>

    <!-- Modal de Detalhes do Produto -->
    <div v-if="showDetailsModal" class="modal-backdrop fade show" @click="closeDetailsModal" style="z-index: 9999;"></div>
    <div v-if="showDetailsModal" class="modal fade show d-block" tabindex="-1" aria-labelledby="productDetailsModalLabel" aria-hidden="true" style="z-index: 10000;">
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="productDetailsModalLabel">
              <i class="fas fa-info-circle me-2"></i>
              Detalhes do Produto
            </h5>
            <button type="button" class="btn-close" @click="closeDetailsModal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div v-if="loadingDetails" class="text-center py-4">
              <div class="spinner-border spinner-teal" role="status">
                <span class="visually-hidden">Carregando...</span>
              </div>
              <p class="mt-2">Carregando detalhes do produto...</p>
            </div>
            <div v-else-if="selectedProduct" class="row">
              <div class="col-md-6">
                <div class="product-image-container text-center mb-3">
                  <template v-if="selectedProduct.image">
                    <img 
                      :src="selectedProduct.image" 
                      :alt="selectedProduct.description"
                      class="img-fluid product-detail-image"
                      @error="handleImageError"
                    >
                    <div class="no-image-placeholder" style="display: none;">
                      <i class="fas fa-image fa-3x text-muted"></i>
                      <p class="text-muted mt-2">Imagem não disponível</p>
                    </div>
                  </template>
                  <template v-else>
                    <div class="no-image-placeholder">
                      <i class="fas fa-image fa-3x text-muted"></i>
                      <p class="text-muted mt-2">Imagem não disponível</p>
                    </div>
                  </template>
                </div>
              </div>
              <div class="col-md-6">
                <div class="product-details">
                  <h6 class="text-primary mb-3">{{ selectedProduct.description || 'Sem descrição' }}</h6>
                  
                  <div class="detail-item mb-2">
                    <strong>ID:</strong> {{ selectedProduct.id }}
                  </div>
                  
                  <div class="detail-item mb-2" v-if="selectedProduct.gtin">
                    <strong>GTIN:</strong> {{ selectedProduct.gtin }}
                  </div>
                  
                  <div class="detail-item mb-2 d-flex align-items-center justify-content-between">
                    <div>
                      <strong>Categoria - API:</strong> {{ getCategoryNameFromProduct(selectedProduct) }}
                    </div>
                    <i 
                      v-if="!hasCategoryApi(selectedProduct)" 
                      class="fas fa-edit text-primary cursor-pointer ms-2" 
                      style="cursor: pointer;"
                      @click="openEditCategoryApiModal(selectedProduct)"
                      title="Editar categoria API"
                    ></i>
                  </div>
                  
                  <div class="detail-item mb-2">
                    <strong>Categoria:</strong> {{ getCategoryName(selectedProduct) }}
                  </div>
                  
                  <div v-if="selectedProduct.expires_at" class="detail-item mb-2">
                    <strong>Validade do preço:</strong>
                    <span class="ms-2 fw-bold"
                      :class="isExpired(selectedProduct.expires_at) ? 'text-danger' : 'text-success'">
                      {{ formatDate(selectedProduct.expires_at) }}
                      <span v-if="isExpired(selectedProduct.expires_at)" class="text-danger ms-1">(expirado)</span>
                    </span>
                  </div>

                  <div v-if="selectedProduct.min_price || selectedProduct.max_price || selectedProduct.avg_price" class="detail-item mb-2">
                    <strong>Informações de Preço:</strong>
                    <div class="mt-2">
                      <div v-if="selectedProduct.avg_price" class="mb-2 p-2 bg-light rounded">
                        <div class="d-flex justify-content-between align-items-center">
                          <span class="text-muted">Preço Médio:</span>
                          <span class="text-success fw-bold fs-6">R$ {{ formatPrice(selectedProduct.avg_price) }}</span>
                        </div>
                      </div>
                      <div v-if="selectedProduct.min_price" class="mb-2 p-2 bg-light rounded">
                        <div class="d-flex justify-content-between align-items-center">
                          <span class="text-muted">Preço Mínimo:</span>
                          <span class="text-info fw-bold">R$ {{ formatPrice(selectedProduct.min_price) }}</span>
                        </div>
                      </div>
                      <div v-if="selectedProduct.max_price" class="mb-2 p-2 bg-light rounded">
                        <div class="d-flex justify-content-between align-items-center">
                          <span class="text-muted">Preço Máximo:</span>
                          <span class="text-warning fw-bold">R$ {{ formatPrice(selectedProduct.max_price) }}</span>
                        </div>
                      </div>
                      <div v-if="selectedProduct.min_price && selectedProduct.max_price" class="mt-2">
                        <span class="text-muted">Faixa de Preço: </span>
                        <span class="text-success fw-bold">
                          R$ {{ formatPrice(selectedProduct.min_price) }} a R$ {{ formatPrice(selectedProduct.max_price) }}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div class="detail-item mb-2">
                    <strong>Descrição:</strong>
                    <p class="text-muted mt-1 mb-0">
                      {{ selectedProduct.description || 'Sem descrição disponível' }}
                    </p>
                  </div>
                  
                  <div v-if="selectedProduct.image" class="detail-item mb-2">
                    <strong>Imagem:</strong>
                    <div class="mt-1">
                      <a :href="selectedProduct.image" target="_blank" class="text-primary">
                        <i class="fas fa-external-link-alt me-1"></i>
                        Ver imagem original
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="text-center py-4">
              <div class="alert alert-danger">
                <i class="fas fa-exclamation-triangle me-2"></i>
                Erro ao carregar detalhes do produto
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeDetailsModal">Fechar</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Adicionar Produto -->
    <div v-if="showAddProductModal" class="modal-backdrop fade show" @click="closeAddModal" style="z-index: 9999;"></div>
    <div v-if="showAddProductModal" class="modal fade show d-block" tabindex="-1" aria-labelledby="addProductModalLabel" aria-hidden="true" style="z-index: 10000;">
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="addProductModalLabel">
              <i class="fas fa-plus me-2"></i>
              Adicionar Novo Produto
            </h5>
            <button type="button" class="btn-close" @click="closeAddModal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div v-if="loadingAdd" class="text-center py-4">
              <div class="spinner-border spinner-teal" role="status">
                <span class="visually-hidden">Carregando...</span>
              </div>
              <p class="mt-2">Cadastrando produto...</p>
            </div>
            <div v-else-if="addError" class="alert alert-danger">
              {{ addError }}
            </div>
            <div v-else>
              <form @submit.prevent="addProduct">
                <div class="row">
                  <div class="col-12 mb-3">
                    <label for="productGtin" class="form-label">GTIN (Código de Barras) *</label>
                    <input 
                      type="text" 
                      class="form-control" 
                      id="productGtin"
                      v-model="newProduct.gtin"
                      placeholder="Ex: 7891100794905"
                      required
                      maxlength="14"
                      pattern="[0-9]*"
                    >
                    <small class="form-text text-muted">Digite o código GTIN (código de barras) do produto</small>
                  </div>
                  <div class="col-12 mb-3">
                    <label class="form-label">Token *</label>
                    <token-selector v-model="selectedToken" />
                    <small class="form-text text-muted">Selecione o token que será usado para cadastrar o produto</small>
                  </div>
                </div>
              </form>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeAddModal">Cancelar</button>
            <button 
              type="button" 
              class="btn btn-custom-gradient" 
              @click="addProduct"
              :disabled="loadingAdd || !newProduct.gtin || !selectedToken"
            >
              <i class="fas fa-save me-1"></i>
              Cadastrar Produto
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal para Editar Categoria API -->
    <div v-if="showEditCategoryApiModal" class="modal-backdrop fade show" @click="closeEditCategoryApiModal" style="z-index: 9999;"></div>
    <div v-if="showEditCategoryApiModal" class="modal fade show d-block" tabindex="-1" aria-labelledby="editCategoryApiModalLabel" aria-hidden="true" style="z-index: 10000;">
      <div class="modal-dialog modal-dialog-centered" role="document" style="max-width: 500px;">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="editCategoryApiModalLabel">
              <i class="fas fa-edit me-2"></i>
              Editar Categoria API
            </h5>
            <button type="button" class="btn-close" @click="closeEditCategoryApiModal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div v-if="loadingCategories" class="text-center py-4">
              <div class="spinner-border spinner-teal" role="status">
                <span class="visually-hidden">Carregando...</span>
              </div>
              <p class="mt-2">Carregando categorias disponíveis...</p>
            </div>
            <div v-else-if="editCategoryApiError" class="alert alert-danger">
              {{ editCategoryApiError }}
            </div>
            <div v-else>
              <div class="mb-3">
                <label class="form-label">Produto</label>
                <input 
                  type="text" 
                  class="form-control" 
                  :value="editingProductForCategoryApi ? (editingProductForCategoryApi.description || 'N/A') : ''" 
                  readonly
                />
              </div>
              <div class="mb-3">
                <label class="form-label">Selecione a Categoria API</label>
                <select 
                  class="form-select" 
                  v-model="selectedCategoryApiId"
                  :disabled="loadingUpdateCategoryApi"
                >
                  <option value="">Selecione uma categoria</option>
                  <option 
                    v-for="categoria in availableCategories" 
                    :key="categoria.id" 
                    :value="categoria.id"
                  >
                    {{ categoria.description || categoria.nome || categoria.name || 'Sem descrição' }}
                  </option>
                </select>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button 
              type="button" 
              class="btn btn-secondary" 
              @click="closeEditCategoryApiModal"
              :disabled="loadingUpdateCategoryApi"
            >
              Cancelar
            </button>
            <button 
              type="button" 
              class="btn btn-primary" 
              @click="updateProductCategoryApi"
              :disabled="!selectedCategoryApiId || loadingUpdateCategoryApi"
            >
              <span v-if="loadingUpdateCategoryApi" class="spinner-border spinner-border-sm me-1"></span>
              Salvar
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { API_BASE_URL, API_ENDPOINTS, apiRequest, handleApiResponse } from "@/config/api.js";
import { showSuccessAlert, showErrorAlert } from "@/utils/swal.js";
import TokenSelector from "@/views/components/TokenSelector.vue";

export default {
  name: "produtos-table",
  components: { TokenSelector },
  data() {
    return {
      produtos: [],
      loading: false,
      error: null,
      // Busca
      searchQuery: '',
      isSearching: false,
      searchDebounce: null,
      showAddProductModal: false,
      loadingAdd: false,
      addError: null,
      showDetailsModal: false,
      selectedProduct: null,
      loadingDetails: false,
      // Editar Categoria API
      showEditCategoryApiModal: false,
      editingProductForCategoryApi: null,
      availableCategories: [],
      selectedCategoryApiId: null,
      loadingCategories: false,
      loadingUpdateCategoryApi: false,
      editCategoryApiError: null,
      // Paginação
      currentPage: 1,
      perPage: 5,
      totalPages: 1,
      totalProducts: 0,
      allProducts: [], // Armazena todos os produtos para simulação de paginação
      newProduct: {
        gtin: ''
      },
      selectedToken: null
    };
  },
  mounted() {
    this.loadProdutos(1);
  },
  methods: {
    async loadProdutos(page = 1) {
      this.loading = true;
      this.error = null;
      this.currentPage = page;
      
      try {
        // Tenta primeiro com paginação real
        const response = await apiRequest(`${API_ENDPOINTS.PRODUCT}?per_page=${this.perPage}&page=${page}`, {
          method: 'GET'
        });
        
        const data = await handleApiResponse(response);
        
        console.log('Resposta completa da API:', data);
        
        // Verifica se a resposta tem estrutura de paginação
        if (data.data && Array.isArray(data.data)) {
          // Estrutura com paginação real
          this.produtos = data.data;
          this.totalProducts = data.total || data.total_products || 0;
          this.totalPages = data.last_page || data.total_pages || Math.ceil(this.totalProducts / this.perPage);
          this.currentPage = data.current_page || page;
          console.log('Usando paginação real da API');
        } else {
          // Usa simulação de paginação
          await this.loadProductsWithSimulation(page);
          console.log('Usando simulação de paginação');
        }
        
        console.log('Produtos carregados:', this.produtos);
        console.log('Paginação:', {
          currentPage: this.currentPage,
          totalPages: this.totalPages,
          totalProducts: this.totalProducts,
          perPage: this.perPage
        });
        
        // Debug: verificar estrutura do primeiro produto
        if (this.produtos.length > 0) {
          console.log('Primeiro produto:', this.produtos[0]);
          console.log('Preço do primeiro produto:', this.produtos[0].price);
        }
        
        // Se não há preços nos produtos, tenta carregar separadamente
        if (this.produtos.length > 0 && !this.produtos[0].price) {
          await this.loadPricesForProducts();
        }
        
      } catch (error) {
        console.error('Erro ao carregar produtos:', error);
        this.error = error.message || 'Erro ao carregar produtos';
      } finally {
        this.loading = false;
      }
    },

    // Dispara a busca com debounce ao digitar
    onSearchInput() {
      clearTimeout(this.searchDebounce);
      const query = this.searchQuery.trim();

      // Se o campo ficou vazio, volta para a listagem normal
      if (query === '') {
        this.clearSearch();
        return;
      }

      this.searchDebounce = setTimeout(() => {
        this.performSearch();
      }, 400);
    },

    // Executa a busca de produtos via API /product/search?q=
    async performSearch() {
      const query = this.searchQuery.trim();

      if (query === '') {
        this.clearSearch();
        return;
      }

      clearTimeout(this.searchDebounce);
      this.loading = true;
      this.isSearching = true;
      this.error = null;

      try {
        const response = await apiRequest(
          `${API_ENDPOINTS.PRODUCT_SEARCH}?q=${encodeURIComponent(query)}`,
          { method: 'GET' }
        );

        const data = await handleApiResponse(response);

        // Normaliza a resposta (array direto ou paginado)
        const results = Array.isArray(data)
          ? data
          : (data.data || data.produtos || data.products || []);

        this.produtos = results;
        this.totalProducts = data.total || data.total_products || results.length;
        this.totalPages = 1;
        this.currentPage = 1;

        // Carrega preços se não vierem na resposta
        if (this.produtos.length > 0 && !this.produtos[0].price) {
          await this.loadPricesForProducts();
        }
      } catch (error) {
        console.error('Erro ao buscar produtos:', error);
        this.error = error.message || 'Erro ao buscar produtos';
        this.produtos = [];
      } finally {
        this.loading = false;
      }
    },

    // Limpa a busca e recarrega a listagem paginada
    clearSearch() {
      clearTimeout(this.searchDebounce);
      this.searchQuery = '';
      this.isSearching = false;
      this.allProducts = [];
      this.loadProdutos(1);
    },

    // Atualizar: respeita o estado de busca atual
    refreshList() {
      if (this.isSearching && this.searchQuery.trim() !== '') {
        this.performSearch();
      } else {
        this.allProducts = [];
        this.loadProdutos(this.currentPage);
      }
    },

    async loadPricesForProducts() {
      try {
        console.log('Carregando preços separadamente...');
        
        // Busca todos os preços de uma vez
        const allPricesResponse = await apiRequest(API_ENDPOINTS.PRICE, {
          method: 'GET'
        });
        
        const allPricesData = await handleApiResponse(allPricesResponse);
        console.log('Todos os preços carregados:', allPricesData);
        
        // Mapeia os preços para os produtos
        if (Array.isArray(allPricesData)) {
          allPricesData.forEach(priceItem => {
            const produtoIndex = this.produtos.findIndex(p => p.id === priceItem.id_product);
            if (produtoIndex !== -1) {
              this.$set(this.produtos[produtoIndex], 'price', priceItem.price);
              this.$set(this.produtos[produtoIndex], 'expires_at', priceItem.expires_at || null);
              console.log(`Preço ${priceItem.price} adicionado ao produto ${priceItem.id_product}`);
            }
          });
        }
        
      } catch (error) {
        console.error('Erro ao carregar preços:', error);
        // Se falhar, tenta buscar individualmente
        await this.loadPricesIndividually();
      }
    },

    async loadPricesIndividually() {
      try {
        console.log('Tentando carregar preços individualmente...');
        
        for (let i = 0; i < this.produtos.length; i++) {
          const produto = this.produtos[i];
          
          try {
            // Tenta buscar o preço usando o endpoint de detalhes do produto
            const productDetailsResponse = await apiRequest(`${API_ENDPOINTS.PRODUCT}/${produto.id}`, {
              method: 'GET'
            });
            
            const productDetails = await handleApiResponse(productDetailsResponse);
            console.log(`Detalhes do produto ${produto.id}:`, productDetails);
            
            // Verifica se o preço está nos detalhes
            if (productDetails && productDetails.price !== undefined) {
              this.$set(this.produtos[i], 'price', productDetails.price);
              this.$set(this.produtos[i], 'expires_at', productDetails.expires_at || null);
            }
            
          } catch (error) {
            console.error(`Erro ao carregar detalhes do produto ${produto.id}:`, error);
          }
        }
        
      } catch (error) {
        console.error('Erro ao carregar preços individualmente:', error);
      }
    },

    
    getStatusColor(status) {
      if (status === true || status === 'ativo' || status === 'active') {
        return 'success';
      } else if (status === false || status === 'inativo' || status === 'inactive') {
        return 'secondary';
      }
      return 'info';
    },
    
    getStatusText(status) {
      if (status === true || status === 'ativo' || status === 'active') {
        return 'Ativo';
      } else if (status === false || status === 'inativo' || status === 'inactive') {
        return 'Inativo';
      }
      return 'Desconhecido';
    },
    
    getCategoryNameFromProduct(produto) {
      if (!produto) return 'Sem categoria';
      
      // Verifica se a categoria da API vem como objeto relacionado no produto
      if (produto.category_api) {
        // Se for objeto com description, prioriza description
        if (typeof produto.category_api === 'object') {
          return produto.category_api.description || produto.category_api.nome || produto.category_api.name || 'Sem descrição';
        }
        // Se for string, retorna como está
        if (typeof produto.category_api === 'string') {
          return produto.category_api;
        }
      }
      
      return 'Sem categoria';
    },
    
    getCategoryName(produto) {
      if (!produto) return 'Sem categoria';
      
      // Verifica se a categoria vem como objeto relacionado no produto
      if (produto.category) {
        // Se for objeto com description, prioriza description
        if (typeof produto.category === 'object') {
          return produto.category.description || produto.category.nome || produto.category.name || 'Sem descrição';
        }
        // Se for string, retorna como está
        if (typeof produto.category === 'string') {
          return produto.category;
        }
      }
      
      return 'Sem categoria';
    },
    
    formatPriceRange(produto) {
      if (produto.min_price && produto.max_price) {
        return `R$ ${this.formatPrice(produto.min_price)} a R$ ${this.formatPrice(produto.max_price)}`;
      } else if (produto.min_price) {
        return `A partir de R$ ${this.formatPrice(produto.min_price)}`;
      } else if (produto.max_price) {
        return `Até R$ ${this.formatPrice(produto.max_price)}`;
      } else if (produto.avg_price) {
        return `R$ ${this.formatPrice(produto.avg_price)}`;
      }
      return 'N/A';
    },
    
    async viewDetails(produto) {
      this.showDetailsModal = true;
      this.selectedProduct = null;
      this.loadingDetails = true;
      
      try {
        const response = await apiRequest(`${API_ENDPOINTS.PRODUCT}/${produto.id}`, {
          method: 'GET'
        });
        
        const data = await handleApiResponse(response);
        
        // Preserva a categoria da API do produto original caso não venha nos detalhes
        if (!data.category_api && produto.category_api) {
          data.category_api = produto.category_api;
        }
        
        // Preserva a categoria do produto original caso não venha nos detalhes
        if (!data.category && produto.category) {
          data.category = produto.category;
        }
        
        // Preserva o id_category do produto original caso não venha nos detalhes
        if (!data.id_category && produto.id_category) {
          data.id_category = produto.id_category;
        }
        
        this.selectedProduct = data;
        
        console.log('Detalhes do produto carregados:', data);
        console.log('Categoria preservada:', data.category_api || data.id_category);
        
      } catch (error) {
        console.error('Erro ao carregar detalhes do produto:', error);
        // Se falhar, usa os dados da lista como fallback
        this.selectedProduct = produto;
        // Silenciosamente usa os dados da lista, não precisa mostrar alerta
        console.warn('Erro ao carregar detalhes completos do produto. Mostrando dados da lista.');
      } finally {
        this.loadingDetails = false;
      }
    },

    closeDetailsModal() {
      this.showDetailsModal = false;
      this.selectedProduct = null;
    },

         handleImageError(event) {
       try {
         console.log('Erro ao carregar imagem, mostrando placeholder...');
         
         // Se a imagem falhar ao carregar, esconde a imagem
         if (event.target && event.target.style) {
           event.target.style.display = 'none';
         }
         
         // Procura pelo placeholder no container da imagem
         const imageContainer = event.target?.parentElement;
         if (imageContainer) {
           const placeholder = imageContainer.querySelector('.no-image-placeholder');
           if (placeholder && placeholder.style) {
             placeholder.style.display = 'block';
           }
         }
       } catch (error) {
         console.warn('Erro ao manipular falha de imagem:', error);
       }
     },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      try {
        // Datas no formato YYYY-MM-DD devem ser tratadas como data local
        const parts = dateString.split('T')[0].split('-');
        if (parts.length === 3) {
          return `${parts[2]}/${parts[1]}/${parts[0]}`;
        }
        return new Date(dateString).toLocaleDateString('pt-BR');
      } catch {
        return dateString;
      }
    },

    isExpired(dateString) {
      if (!dateString) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const parts = dateString.split('T')[0].split('-');
      const expDate = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
      return expDate < today;
    },

    formatPrice(price) {
      console.log('formatPrice recebeu:', price, 'tipo:', typeof price);
      
      if (price === null || price === undefined || price === '') {
        return '0,00';
      }
      
      // Se for string, converte para número
      const numericPrice = typeof price === 'string' ? parseFloat(price) : price;
      
      if (isNaN(numericPrice)) {
        console.log('Preço inválido:', price);
        return '0,00';
      }
      
      return numericPrice.toFixed(2).replace('.', ',');
    },

    getProductImageUrl(product) {
      if (!product) {
        console.log('getProductImageUrl: produto é null/undefined');
        return null;
      }
      
      console.log('getProductImageUrl: verificando produto:', product);
      console.log('getProductImageUrl: image_url:', product.image_url);
      console.log('getProductImageUrl: image_path:', product.image_path);
      console.log('getProductImageUrl: image:', product.image);
      
      // Se já tem uma URL completa
      if (product.image_url && product.image_url.startsWith('http')) {
        console.log('getProductImageUrl: retornando image_url:', product.image_url);
        return product.image_url;
      }
      
      // Se tem image_path, constrói a URL completa
      if (product.image_path) {
        const url = `${API_BASE_URL.replace('/api', '')}/${product.image_path}`;
        console.log('getProductImageUrl: retornando image_path construído:', url);
        return url;
      }
      
      // Se tem image como URL completa
      if (product.image && product.image.startsWith('http')) {
        console.log('getProductImageUrl: retornando image como URL:', product.image);
        return product.image;
      }
      
      // Se tem image como caminho relativo
      if (product.image && !product.image.startsWith('http')) {
        const url = `${API_BASE_URL.replace('/api', '')}/${product.image}`;
        console.log('getProductImageUrl: retornando image como caminho relativo:', url);
        return url;
      }
      
      console.log('getProductImageUrl: nenhuma imagem encontrada, retornando null');
      return null;
    },

    showAddModal() {
      this.showAddProductModal = true;
      this.addError = null;
      this.selectedToken = null;
      this.newProduct = {
        gtin: ''
      };
    },

    closeAddModal() {
      this.showAddProductModal = false;
      this.addError = null;
      this.selectedToken = null;
      this.newProduct = {
        gtin: ''
      };
    },

    async addProduct() {
      if (!this.newProduct.gtin || this.newProduct.gtin.trim() === '') {
        this.addError = 'O GTIN é obrigatório';
        return;
      }

      // Valida se o GTIN contém apenas números
      if (!/^\d+$/.test(this.newProduct.gtin.trim())) {
        this.addError = 'O GTIN deve conter apenas números';
        return;
      }

      if (!this.selectedToken) {
        this.addError = 'Selecione um token';
        return;
      }

      this.loadingAdd = true;
      this.addError = null;

      try {
        // Envia o GTIN e o token para a API
        const response = await apiRequest(API_ENDPOINTS.PRODUCT, {
          method: 'POST',
          body: JSON.stringify({
            gtin: this.newProduct.gtin.trim(),
            token: this.selectedToken.token
          })
        });

        await handleApiResponse(response);

        // Sucesso! Fecha o modal e recarrega a lista
        this.closeAddModal();
        this.loadProdutos();
        
        // Mostra mensagem de sucesso com SweetAlert
        await showSuccessAlert(
          'Produto cadastrado com sucesso!',
          'O produto foi adicionado ao sistema.',
          3000
        );

      } catch (error) {
        console.error('Erro ao cadastrar produto:', error);
        this.addError = error.message || 'Erro ao cadastrar produto';
      } finally {
        this.loadingAdd = false;
      }
    },
     getProductPrice(produto) {
       console.log('Verificando preço para produto:', produto);
       
       // Verifica diferentes possíveis nomes de propriedade para preço
       const possiblePriceProps = ['price', 'preco', 'valor', 'value', 'price_value'];
       
       // Verifica propriedades diretas
       for (const prop of possiblePriceProps) {
         if (produto[prop] !== null && produto[prop] !== undefined && produto[prop] !== '') {
           console.log(`Preço encontrado em ${prop}:`, produto[prop]);
           return produto[prop];
         }
       }
       
       // Verifica se há um objeto de preço aninhado
       if (produto.price_data && typeof produto.price_data === 'object') {
         for (const prop of possiblePriceProps) {
           if (produto.price_data[prop] !== null && produto.price_data[prop] !== undefined && produto.price_data[prop] !== '') {
             console.log(`Preço encontrado em price_data.${prop}:`, produto.price_data[prop]);
             return produto.price_data[prop];
           }
         }
       }
       
       // Verifica se há um objeto de preços (array)
       if (produto.prices && Array.isArray(produto.prices) && produto.prices.length > 0) {
         const firstPrice = produto.prices[0];
         for (const prop of possiblePriceProps) {
           if (firstPrice[prop] !== null && firstPrice[prop] !== undefined && firstPrice[prop] !== '') {
             console.log(`Preço encontrado em prices[0].${prop}:`, firstPrice[prop]);
             return firstPrice[prop];
           }
         }
       }
       
       console.log('Nenhum preço encontrado para produto:', produto);
       return null;
     },

     // Métodos de paginação
     async goToPage(page) {
       if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
         await this.loadProdutos(page);
       }
     },

     async nextPage() {
       if (this.currentPage < this.totalPages) {
         await this.goToPage(this.currentPage + 1);
       }
     },

     async prevPage() {
       if (this.currentPage > 1) {
         await this.goToPage(this.currentPage - 1);
       }
     },

     async firstPage() {
       await this.goToPage(1);
     },

     async lastPage() {
       await this.goToPage(this.totalPages);
     },

     // Gera array de páginas para exibição
     getPageNumbers() {
       const pages = [];
       const maxVisiblePages = 5;
       
       if (this.totalPages <= maxVisiblePages) {
         // Se tem poucas páginas, mostra todas
         for (let i = 1; i <= this.totalPages; i++) {
           pages.push(i);
         }
       } else {
         // Se tem muitas páginas, mostra apenas algumas
         let start = Math.max(1, this.currentPage - 2);
         let end = Math.min(this.totalPages, start + maxVisiblePages - 1);
         
         // Ajusta o início se necessário
         if (end - start < maxVisiblePages - 1) {
           start = Math.max(1, end - maxVisiblePages + 1);
         }
         
         for (let i = start; i <= end; i++) {
           pages.push(i);
         }
       }
       
       console.log('Páginas geradas:', pages);
       return pages;
     },

     // Simula paginação quando a API não suporta
     simulatePagination(allProducts, page) {
       const startIndex = (page - 1) * this.perPage;
       const endIndex = startIndex + this.perPage;
       return allProducts.slice(startIndex, endIndex);
     },

     // Carrega produtos com simulação de paginação
     async loadProductsWithSimulation(page = 1) {
       if (this.allProducts.length === 0) {
         // Primeira vez carregando - busca todos os produtos
         const response = await apiRequest(API_ENDPOINTS.PRODUCT, {
           method: 'GET'
         });
         
         const data = await handleApiResponse(response);
         this.allProducts = Array.isArray(data) ? data : (data.data || data.produtos || data.products || []);
       }
       
       // Aplica paginação simulada
       this.totalProducts = this.allProducts.length;
       this.totalPages = Math.ceil(this.totalProducts / this.perPage);
       this.currentPage = page;
       this.produtos = this.simulatePagination(this.allProducts, page);
       
       console.log(`Simulando paginação: página ${page}, mostrando ${this.produtos.length} de ${this.totalProducts} produtos`);
     },
     
     hasCategoryApi(produto) {
       if (!produto) return false;
       return !!produto.category_api && (
         (typeof produto.category_api === 'object' && (produto.category_api.description || produto.category_api.nome || produto.category_api.name)) ||
         (typeof produto.category_api === 'string' && produto.category_api.trim() !== '')
       );
     },
     
     async openEditCategoryApiModal(produto) {
       console.log('Abrindo modal de edição de categoria API para produto:', produto);
       this.editingProductForCategoryApi = produto;
       this.selectedCategoryApiId = null;
       this.editCategoryApiError = null;
       this.showEditCategoryApiModal = true;
       await this.loadAvailableCategories();
     },
     
     closeEditCategoryApiModal() {
       this.showEditCategoryApiModal = false;
       this.editingProductForCategoryApi = null;
       this.selectedCategoryApiId = null;
       this.availableCategories = [];
       this.editCategoryApiError = null;
     },
     
     async loadAvailableCategories() {
       this.loadingCategories = true;
       this.editCategoryApiError = null;
       
       try {
         console.log('Carregando categorias do endpoint:', API_ENDPOINTS.CATEGORY_API);
         const response = await apiRequest(API_ENDPOINTS.CATEGORY_API, {
           method: 'GET'
         });
         
         const data = await handleApiResponse(response);
         console.log('Categorias recebidas da API:', data);
         this.availableCategories = Array.isArray(data) ? data : (data.data || data.categorias || []);
         console.log('Categorias disponíveis:', this.availableCategories);
         
       } catch (error) {
         console.error('Erro ao carregar categorias API:', error);
         this.editCategoryApiError = error.message || 'Erro ao carregar categorias disponíveis';
       } finally {
         this.loadingCategories = false;
       }
     },
     
     async updateProductCategoryApi() {
       if (!this.selectedCategoryApiId || !this.editingProductForCategoryApi) {
         return;
       }
       
       this.loadingUpdateCategoryApi = true;
       this.editCategoryApiError = null;
       
       try {
         const response = await apiRequest(`${API_ENDPOINTS.PRODUCT}/${this.editingProductForCategoryApi.id}`, {
           method: 'PUT',
           body: JSON.stringify({
             id_category_api: this.selectedCategoryApiId
           })
         });
         
         await handleApiResponse(response);
         
         // Sucesso! Fecha o modal e recarrega a lista
         this.closeEditCategoryApiModal();
         this.loadProdutos(this.currentPage);
         
         await showSuccessAlert(
           'Categoria API atualizada com sucesso!',
           'A categoria API do produto foi atualizada.',
           3000
         );
         
       } catch (error) {
         console.error('Erro ao atualizar categoria API:', error);
         this.editCategoryApiError = error.message || 'Erro ao atualizar categoria API';
         await showErrorAlert(
           'Erro ao atualizar categoria API',
           error.message || 'Não foi possível atualizar a categoria API do produto.'
         );
       } finally {
         this.loadingUpdateCategoryApi = false;
       }
     },
  },
};
</script>

<style scoped>
.modal-backdrop {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  background-color: rgba(0, 0, 0, 0.5) !important;
}

.modal {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.modal-dialog {
  width: 100%;
  margin: 0.5rem auto;
}

@media (max-width: 576px) {
  .modal-dialog {
    max-width: calc(100% - 1rem);
    margin: 0.5rem;
  }
}

.btn-custom-gradient {
  background: linear-gradient(310deg, #17c1e8 0%, #20c997 100%);
  border: none;
  color: white;
  font-weight: 600;
  transition: all 0.3s ease;
}

.btn-custom-gradient:hover {
  background: linear-gradient(310deg, #15a8d1 0%, #1bb88a 100%);
  color: white;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(23, 193, 232, 0.3);
}

.btn-custom-gradient:active {
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(23, 193, 232, 0.3);
}

.spinner-teal {
  color: #20c997 !important;
}

.gap-2 {
  gap: 0.5rem;
}

.search-group {
  position: relative;
  width: 100%;
  max-width: 260px;
  flex-shrink: 1;
}

.search-group .search-icon {
  position: absolute;
  top: 50%;
  left: 14px;
  transform: translateY(-50%);
  font-size: 0.8rem;
  color: #adb5bd;
  pointer-events: none;
}

.search-input {
  width: 100%;
  height: 38px;
  /* padding-right reserva espaço fixo para o botão limpar, evitando "pulo" no layout */
  padding: 0 38px 0 36px;
  font-size: 0.875rem;
  color: #495057;
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 20px;
  outline: none;
  transition: border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;
}

.search-input::placeholder {
  color: #adb5bd;
}

.search-input:focus {
  background-color: #fff;
  border-color: #20c997;
  box-shadow: 0 0 0 3px rgba(32, 201, 151, 0.15);
}

.search-clear {
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #adb5bd;
  font-size: 0.75rem;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.search-clear:hover {
  background-color: #e9ecef;
  color: #495057;
}

.product-detail-image {
  max-width: 100%;
  max-height: 300px;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.no-image-placeholder {
  padding: 40px 20px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 2px dashed #dee2e6;
}

.product-details {
  padding: 0 10px;
}

.detail-item {
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 8px;
}

.detail-item:last-child {
  border-bottom: none;
}
 
 /* Modal de altura limitada */
 .modal-dialog.modal-lg {
   max-height: 80vh;
 }
 
 .modal-dialog.modal-lg .modal-content {
   max-height: 80vh;
   overflow-y: auto;
 }
 
 .modal-dialog.modal-lg .modal-body {
   max-height: 60vh;
   overflow-y: auto;
 }

 /* Estilos da paginação com gradiente teal */
 .pagination .page-link {
   border: none;
   color: #6c757d;
   background-color: white;
   transition: all 0.3s ease;
   box-shadow: 0 0 0 1px rgba(23, 193, 232, 0.3);
 }

 .pagination .page-link:hover {
   background: linear-gradient(310deg, #17c1e8 0%, #20c997 100%);
   color: white;
   border: none;
   transform: translateY(-1px);
   box-shadow: 0 2px 4px rgba(23, 193, 232, 0.3);
 }

 .pagination .page-item.active .page-link {
   background: linear-gradient(310deg, #17c1e8 0%, #20c997 100%);
   border: none;
   color: white;
   box-shadow: 0 2px 4px rgba(23, 193, 232, 0.3);
 }

 .pagination .page-item.disabled .page-link {
   background: transparent;
   color: #6c757d;
   opacity: 0.5;
   cursor: not-allowed;
 }

 .pagination .page-item.disabled .page-link:hover {
   background: transparent;
   color: #6c757d;
   transform: none;
   box-shadow: none;
 }
</style>
